import { isAxiosError , type AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import {
  normalizeCatalogItemsResponse,
  type CatalogItemFilters,
  type CatalogItemsResponse,
  type CatalogPagination,
  type CatalogItem,
  type CatalogLookups
} from '../types/catalog';
import type { DecimalString } from '../types/subprojects';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const PAGE_SIZES = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const normalizePageSize = (size: number) =>
  PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number]) ? size : DEFAULT_PAGE_SIZE;

const toNumberFilter = (value?: DecimalString | number) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeFilters = (raw: CatalogItemFilters): CatalogItemFilters => ({
  search: raw.search?.trim() || undefined,
  supplier: raw.supplier?.trim() || undefined,
  category: raw.category?.trim() || undefined,
  active: raw.active,
  min_unit_cost: toNumberFilter(raw.min_unit_cost),
  max_unit_cost: toNumberFilter(raw.max_unit_cost),
  min_pvp: toNumberFilter(raw.min_pvp),
  max_pvp: toNumberFilter(raw.max_pvp),
});

const sameFilters = (a: CatalogItemFilters, b: CatalogItemFilters) =>
  ['search', 'supplier', 'category', 'active', 'min_unit_cost', 'max_unit_cost', 'min_pvp', 'max_pvp']
    .every((key) => a[key as keyof CatalogItemFilters] === b[key as keyof CatalogItemFilters]);

export function useCatalogItemsApi(
  initialFilters: CatalogItemFilters = {},
  initialPagination: CatalogPagination = { page: 1, page_size: 50 },
) {
  const { token } = useAuth();
  const [filters, setFilters] = useState<CatalogItemFilters>(initialFilters);
  const [pagination, setPagination] = useState<CatalogPagination>({
  ...initialPagination,
  page_size: normalizePageSize(initialPagination.page_size ?? DEFAULT_PAGE_SIZE),
  total: initialPagination.total ?? 0,
  });
  const [data, setData] = useState<CatalogItemsResponse['items']>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) throw new Error('No hay sesión activa para llamar a la API de catálogo');
      const response = await apiClient.request<Response>({
        ...config,
        url: `/catalog${config.url ?? ''}`,
      });
      return response.data;
    },
    [token],
  );

  const fetchItems = useCallback(
    async (overrides: { filters?: CatalogItemFilters; page?: number; page_size?: number } = {}) => {
      setLoading(true);
      setError(null);

      const normalizedFilters = normalizeFilters({ ...filters, ...(overrides.filters ?? {}) });
      const filtersChanged = !sameFilters(filters, normalizedFilters);
      const nextPageSize = normalizePageSize(overrides.page_size ?? pagination.page_size);
      const nextPage = overrides.page ?? (filtersChanged ? 1 : pagination.page);
      const offset = (nextPage - 1) * nextPageSize;

      try {
        const resp = await request<any>({
          method: 'GET',
          url: '/items',
          params: { ...normalizedFilters, limit: nextPageSize, offset },
        });
        const normalized = normalizeCatalogItemsResponse(resp);
        const computedPageSize = normalizePageSize(normalized.pagination.page_size ?? nextPageSize);
        const computedTotal = normalized.pagination.total ?? pagination.total ?? offset + normalized.items.length;

        setData(normalized.items);
        setFilters((prev) => (sameFilters(prev, normalizedFilters) ? prev : normalizedFilters));
        setPagination((prev) => {
          const next = { ...prev, ...normalized.pagination, total: computedTotal, page: nextPage, page_size: computedPageSize };
          return (
            prev.page === next.page &&
            prev.page_size === next.page_size &&
            prev.total === next.total &&
            prev.total_pages === next.total_pages
          )
            ? prev
            : next;
        });

      return normalized;
      } catch (err) {
        const message = isAxiosError(err) && err.response?.status === 401
          ? 'Sesión caducada o sin permisos. Inicia sesión nuevamente.'
          : 'No pudimos cargar el catálogo. Inténtalo de nuevo en unos segundos.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [request, filters, pagination.page, pagination.page_size],
  );

  useEffect(() => {
    if (!token) return;
    void fetchItems({ filters, page: pagination.page, page_size: pagination.page_size });
  }, [token, filters, pagination.page, pagination.page_size, fetchItems]);

  const goToPage = (page: number) => fetchItems({ page });
  const refresh = () => fetchItems();

  return { data, loading, error, pagination, filters, setFilters, setPagination, goToPage, refresh };
}

export function useCatalogItemActions() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de catálogo');
      }
      const response = await apiClient.request<Response>({
        ...config,
        url: `/catalog${config.url ?? ''}`,
      });
      return response.data;
    },
    [token],
  );

  const updateCatalogItem = useCallback(
    (id: CatalogItem['id'], data: Partial<CatalogItem>) =>
      request<CatalogItem>({
        method: 'PUT',
        url: `/items/${id}`,
        data,
      }),
    [request],
  );

  const deleteCatalogItem = useCallback(
    (id: CatalogItem['id']) =>
      request<void>({
        method: 'DELETE',
        url: `/items/${id}`,
      }),
    [request],
  );

  const createCatalogItem = useCallback(
    (data: Partial<CatalogItem>) =>
      request<CatalogItem>({
        method: 'POST',
        url: '/items',
        data,
      }),
    [request],
  );

  return { updateCatalogItem, deleteCatalogItem, createCatalogItem };
}

export function useCatalogLookups() {
  const { token } = useAuth();
  const [lookups, setLookups] = useState<CatalogLookups>({ suppliers: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) throw new Error('No hay sesión activa para llamar a la API de catálogo');
      const response = await apiClient.request<Response>({
        ...config,
        url: `/catalog${config.url ?? ''}`,
      });
      return response.data;
    },
    [token],
  );

  const fetchLookups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await request<CatalogLookups>({ method: 'GET', url: '/lookups' });
      setLookups({ suppliers: resp.suppliers ?? [], categories: resp.categories ?? [] });
      return resp;
    } catch (err) {
      const message = isAxiosError(err) && err.response?.status === 401
        ? 'Sesión caducada o sin permisos. Inicia sesión nuevamente.'
        : 'No pudimos cargar proveedores y categorías. Inténtalo de nuevo en unos segundos.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { if (!token) return; void fetchLookups(); }, [token, fetchLookups]);

  return { lookups, loading, error, refresh: fetchLookups };
}

type QuickSearchField = 'ref' | 'supplier' | 'description' | 'any';

export function useCatalogQuickSearch() {
  const { token } = useAuth();
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) throw new Error('No hay sesión activa para llamar a la API de catálogo');
      const response = await apiClient.request<Response>({
        ...config,
        url: `/catalog${config.url ?? ''}`,
      });
      return response.data;
    },
    [token],
  );

  const search = useCallback(
    async (params: { query: string; by?: QuickSearchField; limit?: number }) => {
      const query = params.query.trim();
      if (!query) {
        setResults([]);
        return [];
      }
      setLoading(true);
      setError(null);
      try {
        const data = await request<CatalogItem[]>({
          method: 'GET',
          url: '/items/search',
          params: {
            q: query,
            by: params.by ?? 'any',
            limit: params.limit ?? 50,
          },
        });
        setResults(data);
        return data;
      } catch (err) {
        const message = isAxiosError(err) && err.response?.status === 401
          ? 'Sesión caducada o sin permisos. Inicia sesión nuevamente.'
          : 'No pudimos buscar en el catálogo. Inténtalo de nuevo en unos segundos.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [request],
  );

  return { results, loading, error, search, setResults };
}
